// Memory optimization utilities

// Clean up event listeners and timers
export const cleanupEventListeners = () => {
  // Remove all event listeners that might be causing memory leaks
  window.removeEventListener('beforeunload', () => {});
  window.removeEventListener('unload', () => {});
  
  // Clear any pending timers
  const highestTimeoutId = setTimeout(() => {}, 0);
  for (let i = 0; i < highestTimeoutId; i++) {
    clearTimeout(i);
  }
  
  const highestIntervalId = setInterval(() => {}, 0);
  for (let i = 0; i < highestIntervalId; i++) {
    clearInterval(i);
  }
};

// Memory usage monitoring
export const getMemoryUsage = () => {
  if (performance.memory) {
    return {
      used: Math.round(performance.memory.usedJSHeapSize / 1048576), // MB
      total: Math.round(performance.memory.totalJSHeapSize / 1048576), // MB
      limit: Math.round(performance.memory.jsHeapSizeLimit / 1048576) // MB
    };
  }
  return null;
};

// Log memory usage
export const logMemoryUsage = (label = 'Memory Usage') => {
  const memory = getMemoryUsage();
  if (memory) {
    console.log(`${label}:`, {
      used: `${memory.used}MB`,
      total: `${memory.total}MB`,
      limit: `${memory.limit}MB`,
      percentage: `${Math.round((memory.used / memory.limit) * 100)}%`
    });
  }
};

// Force garbage collection (if available)
export const forceGarbageCollection = () => {
  if (window.gc) {
    window.gc();
    console.log('Garbage collection forced');
  } else {
    console.log('Garbage collection not available in this environment');
  }
};

// Optimize images and large objects
export const optimizeLargeObjects = (objects) => {
  return objects.map(obj => {
    // Remove unnecessary properties
    const { __proto__, ...cleanObj } = obj;
    return cleanObj;
  });
};

// Debounce function to prevent excessive function calls
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Throttle function to limit function execution frequency
export const throttle = (func, limit) => {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// Memory leak detection
export const detectMemoryLeaks = () => {
  const memory = getMemoryUsage();
  if (memory && memory.used > memory.limit * 0.8) {
    console.warn('High memory usage detected:', memory);
    return true;
  }
  return false;
};

// Initialize memory monitoring
export const initializeMemoryMonitoring = () => {
  if (process.env.NODE_ENV === 'development') {
    // Monitor memory every 30 seconds in development
    setInterval(() => {
      logMemoryUsage('Development Memory Check');
      if (detectMemoryLeaks()) {
        console.warn('Potential memory leak detected!');
        forceGarbageCollection();
      }
    }, 30000);
  }
};

// Cleanup function for component unmounting
export const componentCleanup = () => {
  cleanupEventListeners();
  forceGarbageCollection();
  logMemoryUsage('After Cleanup');
};
